// services/chat.service.js
import assistantClient, { handleApiError } from '../api/assistantClient';
import { streamChat, createStreamController } from '../utils/sseClient';

const buildContentBlocks = ({ message, contentType = 'text', contentBlocks }) => {
  if (Array.isArray(contentBlocks) && contentBlocks.length > 0) {
    return contentBlocks.map((block, index) => ({
      ...block,
      order: index + 1,
    }));
  }

  return [
    {
      type: contentType,
      data: contentType === 'text' ? { text: message } : { url: message },
      order: 1,
    },
  ];
};

/**
 * Servicio para gestionar Chat y Conversaciones
 */
class ChatService {
  /**
   * Crear una nueva sesión de chat (Chat History)
   * Versión híbrida: intenta nueva API account-based, fallback a la actual
   */
  async createChatHistory({ accountId, userId, assistantId, title, userTimezone, initialMessage }) {
    // Intentar nueva API account-based primero
    if (accountId) {
      try {
        const requestBody = {
          userTimezone: userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          assistantId: assistantId || null,
          title: title || null,
          externalId: null,
          contextVariables: {},
          systemPrompt: null,
          initialMessage: initialMessage || null
        };

        const response = await assistantClient.post(`/accounts/${accountId}/chat/histories`, requestBody);
        return { success: true, data: response.data };
      } catch (error) {
        // Si es 404, la nueva API no está disponible, usar fallback
        if (error.response?.status === 404) {
          console.warn('Nueva API de chat no disponible, usando API legacy');
        } else {
          // Si es otro error, reportarlo
          return { success: false, error: handleApiError(error) };
        }
      }
    }

    // Fallback a API actual
    if (userId) {
      try {
        const response = await assistantClient.post('/chat/histories', {
          user_id: userId,
          assistant_id: assistantId,
          title: title || undefined,
          user_timezone: userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        return { success: true, data: response.data };
      } catch (error) {
        return { success: false, error: handleApiError(error) };
      }
    }

    return { success: false, error: { message: 'accountId o userId requerido' } };
  }

  /**
   * Listar chat histories
   * Versión híbrida: intenta nueva API account-based, fallback a la actual
   */
  async listChatHistories({ accountId, userId, assistantId, projectId, limit = 10, offset = 0, skip = 0, sortBy = 'updated_at', descending = true }) {
    // Intentar nueva API account-based primero
    if (accountId) {
      try {
        const params = {};
        if (assistantId) params.assistant_id = assistantId;
        if (projectId) params.project_id = projectId;
        params.limit = limit;
        params.offset = offset;
        params.sort_by = sortBy;
        params.descending = descending;

        const response = await assistantClient.get(`/accounts/${accountId}/chat/histories`, { params });
        return {
          success: true,
          data: response.data.chatHistories || response.data || [],
          total: response.data.total || 0,
        };
      } catch (error) {
        // Si es 404, la nueva API no está disponible, usar fallback
        if (error.response?.status === 404) {
          console.warn('Nueva API de chat no disponible, usando API legacy');
        } else {
          // Si es otro error, reportarlo
          return { success: false, error: handleApiError(error) };
        }
      }
    }

    // Fallback a API actual
    if (userId) {
      try {
        const params = {};
        if (userId) params.user_id = userId;
        if (assistantId) params.assistant_id = assistantId;
        params.limit = limit;
        params.skip = skip;

        const response = await assistantClient.get('/chat/histories', { params });
        return {
          success: true,
          data: response.data.chatHistories || [],
          total: response.data.total || 0,
        };
      } catch (error) {
        return { success: false, error: handleApiError(error) };
      }
    }

    return { success: false, error: { message: 'accountId o userId requerido' } };
  }

  /**
   * Obtener chat history por ID
   */
  async getChatHistory(chatHistoryId, { userId } = {}) {
    try {
      const params = { chat_history_id: chatHistoryId };
      if (userId) params.user_id = userId;
      const response = await assistantClient.get(`/chat/histories`, { params });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Enviar mensaje y obtener respuesta del assistant
   */
  async sendMessage({ assistantId, chatHistoryId, message, contentType = 'text', contentBlocks, userId, accountId }) {
    try {
      const normalizedContentBlocks = buildContentBlocks({ message, contentType, contentBlocks });

      const requestBody = {
        assistant_id: assistantId,
        chat_history_id: chatHistoryId,
        content_blocks: normalizedContentBlocks,
      };

      // Agregar userId y accountId si están disponibles (requeridos por la nueva API)
      if (userId) {
        requestBody.userId = userId;
      }
      if (accountId) {
        requestBody.accountId = accountId;
      }

      const response = await assistantClient.post('/chat/assistant', requestBody);
      const contentBudget = response?.data?.metadata?.content_budget || response?.data?.metadata?.contentBudget || null;
      return {
        success: true,
        data: {
          ...response.data,
          contentBudget,
        },
      };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Enviar mensaje y obtener respuesta STREAMING del assistant
   * Usa Server-Sent Events (SSE) para recibir la respuesta en chunks
   * 
   * @param {Object} params - Parámetros del mensaje
   * @param {Object} callbacks - Callbacks para manejar el stream
   * @param {Function} callbacks.onContent - Llamado con cada chunk de texto
   * @param {Function} callbacks.onMetadata - Llamado con metadata final
   * @param {Function} callbacks.onDone - Llamado cuando termina el stream
   * @param {Function} callbacks.onError - Llamado si hay error
   * @returns {Object} { controller } - Controller para cancelar el stream
   */
  sendMessageStream({ assistantId, chatHistoryId, message, contentType = 'text', contentBlocks, userId, accountId }, callbacks = {}) {
    const { onContent, onMetadata, onDone, onError } = callbacks;

    const normalizedContentBlocks = buildContentBlocks({ message, contentType, contentBlocks });

    const requestBody = {
      assistant_id: assistantId,
      chat_history_id: chatHistoryId,
      content_blocks: normalizedContentBlocks,
    };

    if (userId) {
      requestBody.userId = userId;
    }
    if (accountId) {
      requestBody.accountId = accountId;
    }

    // Crear controller para poder cancelar
    const controller = createStreamController(120000); // 2 min timeout

    // Iniciar streaming
    streamChat('/chat/assistant/stream', {
      body: requestBody,
      signal: controller.signal,
      onContent,
      onMetadata: (meta) => {
        console.log('🔶 [ChatService] onMetadata called, has callback:', !!onMetadata, 'display_blocks:', meta?.display_blocks);
        if (!onMetadata) return;
        const contentBudget = meta?.content_budget || meta?.contentBudget || null;
        onMetadata({ ...meta, contentBudget });
      },
      onDone,
      onError,
    });

    return { controller };
  }

  /**
   * Obtener mensajes de un chat history
   */
  async getChatMessages(chatHistoryId, { limit = 50, skip = 0 } = {}) {
    try {
      const response = await assistantClient.get(
        `/chat/histories/${chatHistoryId}/messages`,
        { params: { limit, skip } }
      );
      return {
        success: true,
        data: response.data.messages || [],
        total: response.data.total || 0,
      };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener mensajes recientes de un chat
   */
  async getRecentMessages(chatHistoryId, count = 10) {
    try {
      const response = await assistantClient.get(
        `/chat/histories/${chatHistoryId}/messages/recent`,
        { params: { count } }
      );
      return {
        success: true,
        data: response.data.messages || [],
      };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Actualizar título del chat history
   */
  async updateChatHistoryTitle(chatHistoryId, title) {
    try {
      const response = await assistantClient.patch(`/chat/histories/${chatHistoryId}`, {
        title,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Eliminar chat history
   */
  async deleteChatHistory(chatHistoryId) {
    try {
      const response = await assistantClient.delete(`/chat/histories/${chatHistoryId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Crear chat history y enviar primer mensaje
   * Esta función combina la creación del chat history con el envío del primer mensaje
   */
  async createChatHistoryWithMessage({ accountId, userId, assistantId, message, contentType = 'text', userTimezone }) {
    try {
      // Crear el chat history con el mensaje inicial para generar el título
      const chatResult = await this.createChatHistory({
        accountId,
        userId,
        assistantId,
        initialMessage: contentType === 'text' ? message : null,
        userTimezone
      });

      if (!chatResult.success) {
        return chatResult;
      }

      const chatHistoryId = chatResult.data.id;

      // Enviar el primer mensaje
      const messageResult = await this.sendMessage({
        assistantId,
        chatHistoryId,
        message,
        contentType,
        userId,
        accountId
      });

      if (messageResult.success) {
        return {
          success: true,
          data: {
            chatHistory: chatResult.data,
            firstMessage: messageResult.data,
            chatHistoryId
          }
        };
      } else {
        // Si falló el mensaje, intentar limpiar el chat history
        await this.deleteChatHistory(chatHistoryId);
        return messageResult;
      }
    } catch (error) {
      return { success: false, error: handleApiError(error) };
    }
  }
}

export default new ChatService();
